<script setup>
import { onMounted, ref } from 'vue'

/**
 * Reusable mega-menu panel for the Navbar. Encapsulates the frosted-glass
 * styling, slide-in transition, Teleport-to-body (escapes the navbar's
 * backdrop-filter stacking context - without that, our own backdrop-blur
 * would render against the navbar's already-blurred output instead of the
 * actual page content), and the responsive 2-column grid.
 *
 * Two slots: `#left` (link columns) and `#right` (visual cards). Hover events
 * bubble up via emits so the parent owns the open/close timer logic - the
 * panel stays a dumb presentational component.
 *
 * Position: fixed at top-19 (the navbar's bottom edge - same value the
 * mobile drawer + locations sidebar use). Hidden on mobile (`md:block`); the
 * mobile drawer covers the same affordance there.
 *
 * SSR note: Teleport is disabled until after mount. During server rendering
 * there is no real `document.body`, so Vue can't reconcile teleported content
 * across the SSR → client hydration boundary - symptoms include
 * `Hydration node mismatch` warnings and `Cannot read properties of null
 * (reading 'insertBefore')` crashes when the panel later opens. Keeping the
 * Teleport disabled during SSR means the panel renders inline (a noop since
 * `open=false` at boot), hydrates cleanly against the SSR markup, then flips
 * to true teleport on the next tick.
 */
defineProps({
    open: { type: Boolean, default: false },
    ariaLabel: { type: String, required: true },
})

defineEmits(['mouseenter', 'mouseleave'])

const teleportReady = ref(false)
onMounted(() => {
    teleportReady.value = true
})
</script>

<template>
    <Teleport
        :disabled="!teleportReady"
        to="body"
    >
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div
                class="fixed inset-x-0 top-19 z-40 hidden border-b border-zinc-900 bg-zinc-950/80 shadow-2xl shadow-black/40 backdrop-blur-3xl md:block"
                v-if="open"
                role="region"
                :aria-label="ariaLabel"
                @mouseenter="$emit('mouseenter')"
                @mouseleave="$emit('mouseleave')"
            >
                <div class="mx-auto grid w-full max-w-7xl grid-cols-1 gap-x-12 gap-y-8 px-6 py-10 lg:grid-cols-2">
                    <slot name="left" />
                    <slot name="right" />
                </div>
            </div>
        </Transition>
    </Teleport>
</template>
